import { useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  TimePicker,
  Typography,
  message,
} from 'antd';
import { autoGenerateRoomSlots, getShopData } from '@/api/shops';

const { Text } = Typography;

type RoomItem = {
  id: number;
  roomName: string;
  roomSeq?: number;
};

type AutoGeneratePayload = {
  startDate: string;
  endDate: string;
  weekdays: number[];
  roomIds: number[];
  startMinuteOfDay: number;
  endMinuteOfDay: number;
  selectedStartMinutes: number[];
};

type FormValues = {
  startDateRange: [Dayjs, Dayjs];
  weekdays: number[];
  roomIds: number[];
  timeRange: [Dayjs, Dayjs];
};
type Props = {
  onClose: () => void;
  open: boolean;
  shopId: number;
  // onSubmit: (payload: AutoGeneratePayload) => Promise<void> | void;
};



const WEEKDAY_OPTIONS = [
  { label: '월', value: 1 },
  { label: '화', value: 2 },
  { label: '수', value: 3 },
  { label: '목', value: 4 },
  { label: '금', value: 5 },
  { label: '토', value: 6 },
  { label: '일', value: 0 },
];

const DEFAULT_START = dayjs('13:00', 'HH:mm');
const DEFAULT_END = dayjs('20:00', 'HH:mm');
const DEFAULT_DATE_START = dayjs().add(6, 'month').startOf('month');
const DEFAULT_DATE_END = dayjs().add(8, 'month').endOf('month');
const SLOT_UNIT_MINUTES = 60; // 일단 UI 기본값은 60분 기준

function timeToMinuteOfDay(time: Dayjs) {
  return time.hour() * 60 + time.minute();
}

function minuteToLabel(minute: number) {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function buildStartMinutes(startMinuteOfDay: number, endMinuteOfDay: number) {
  const result: number[] = [];
  let cursor = startMinuteOfDay;

  while (cursor + SLOT_UNIT_MINUTES <= endMinuteOfDay) {
    result.push(cursor);
    cursor += SLOT_UNIT_MINUTES;
  }

  return result;
}

export default function OpenSlotAutoGenerateModal({
  open,
  onClose,
  shopId,
  // loading = false,
  
}: Props) {
  const [form] = Form.useForm<FormValues>();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedStartMinutes, setSelectedStartMinutes] = useState<number[]>([]);
  const [roomOptions, setRoomOptions] = useState([])
  const watchedWeekdays = Form.useWatch('weekdays', form);
  const watchedRoomIds = Form.useWatch('roomIds', form);
  const watchedTimeRange = Form.useWatch('timeRange', form);
  const watchedDateRange = Form.useWatch('startDateRange', form);
  const startMinuteOfDay = useMemo(() => {
    if (!watchedTimeRange?.[0]) return null;
    return timeToMinuteOfDay(watchedTimeRange[0]);
  }, [watchedTimeRange]);

  const endMinuteOfDay = useMemo(() => {
    if (!watchedTimeRange?.[1]) return null;
    return timeToMinuteOfDay(watchedTimeRange[1]);
  }, [watchedTimeRange]);

  const slotOptions = useMemo(() => {
    if (startMinuteOfDay == null || endMinuteOfDay == null) {
      return [];
    }
    if (startMinuteOfDay >= endMinuteOfDay) {
      return [];
    }

    return buildStartMinutes(startMinuteOfDay, endMinuteOfDay);
  }, [startMinuteOfDay, endMinuteOfDay]);

  useEffect(() => {
    setSelectedStartMinutes((prev) => {
      const next = prev.filter((minute) => slotOptions.includes(minute));
      if (next.length > 0) {
        return next;
      }
      return [...slotOptions];
    });
  }, [slotOptions]);

  const handleOpen = () => {
    form.resetFields();
    form.setFieldsValue({
      startDateRange: [DEFAULT_DATE_START, DEFAULT_DATE_END],
      weekdays: [2, 3, 4, 5, 6],
      roomIds: [],
      timeRange: [DEFAULT_START, DEFAULT_END],
    });
    setSelectedStartMinutes(buildStartMinutes(780, 1080));
    // setOpen(true);
  };

  const handleClose = () => {
    // setOpen(false);
    form.resetFields();
    setSelectedStartMinutes([]);
  };

  const toggleMinute = (minute: number) => {
    setSelectedStartMinutes((prev) => {
      if (prev.includes(minute)) {
        return prev.filter((item) => item !== minute);
      }
      return [...prev, minute].sort((a, b) => a - b);
    });
  };

  const handleSelectAllSlots = () => {
    setSelectedStartMinutes([...slotOptions]);
  };

  const handleClearAllSlots = () => {
    setSelectedStartMinutes([]);
  }; 

  const fetchRooms = async () => {
    const result = await getShopData(shopId)
    const {
      data: {
        rooms
      }
    
    } = result
    console.log(rooms)
    setRoomOptions(rooms)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!values.startDateRange || !values.startDateRange[0] || !values.startDateRange[1]) {
        message.error('적용 기간을 선택해주세요.');
        return;
      }

      if (!values.weekdays || values.weekdays.length === 0) {
        message.error('요일을 선택해주세요.');
        return;
      }

      if (!values.roomIds || values.roomIds.length === 0) {
        message.error('룸을 선택해주세요.');
        return;
      }

      if (!values.timeRange || !values.timeRange[0] || !values.timeRange[1]) {
        message.error('시간 범위를 선택해주세요.');
        return;
      }

      const startMinute = timeToMinuteOfDay(values.timeRange[0]);
      const endMinute = timeToMinuteOfDay(values.timeRange[1]);

      if (startMinute >= endMinute) {
        message.error('종료 시간은 시작 시간보다 뒤여야 합니다.');
        return;
      }

      if (selectedStartMinutes.length === 0) {
        message.error('최소 1개 이상의 시간 슬롯을 선택해주세요.');
        return;
      }

      setSubmitLoading(true);

      await autoGenerateRoomSlots(shopId, {
        startDate: values.startDateRange[0].format('YYYY-MM-DD'),
        endDate: values.startDateRange[1].format('YYYY-MM-DD'),
        weekdays: values.weekdays,
        roomIds: values.roomIds,
        startMinuteOfDay: startMinute,
        endMinuteOfDay: endMinute,
        selectedStartMinutes: [...selectedStartMinutes].sort((a, b) => a - b),
      });

      message.success('초기 세팅 요청이 반영되었습니다.');
      handleClose();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || '초기 세팅 저장 중 오류가 발생했습니다.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const selectedWeekdayLabels = (watchedWeekdays || [])
    .map((value) => WEEKDAY_OPTIONS.find((item) => item.value === value)?.label)
    .filter(Boolean);

  const selectedRoomLabels = (watchedRoomIds || [])
    .map((roomId) => roomOptions.find((room) => room.id === roomId)?.roomName)
    .filter(Boolean);

  const estimatedCount =
    (watchedWeekdays?.length || 0) *
    (watchedRoomIds?.length || 0) *
    selectedStartMinutes.length;
  useEffect(() => {
    fetchRooms()
  }, [])
  return (
    <>
      

      <Modal
        title="초기 슬롯 세팅"
        open={open}
        onCancel={onClose}
        onOk={handleSubmit}
        okText="생성"
        cancelText="취소"
        confirmLoading={submitLoading}
        width={920}
        destroyOnHidden
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          initialValues={{
            startDateRange: [DEFAULT_DATE_START, DEFAULT_DATE_END],
            weekdays: [2, 3, 4, 5, 6],
            roomIds: [],
            timeRange: [DEFAULT_START, DEFAULT_END],
          }}
        >
          <Row gutter={16}>
            <Col span={14}>
              <Card size="small" title="조건 선택">
                <Form.Item
                  label="적용 기간"
                  name="startDateRange"
                  rules={[{ required: true, message: '적용 기간을 선택해주세요.' }]}
                >
                  <DatePicker.RangePicker
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>
                <Form.Item
                  label="요일"
                  name="weekdays"
                  rules={[{ required: true, message: '요일을 선택해주세요.' }]}
                >
                  <Checkbox.Group options={WEEKDAY_OPTIONS} />
                </Form.Item>

                <Form.Item
                  label="룸 선택"
                  name="roomIds"
                  rules={[{ required: true, message: '룸을 선택해주세요.' }]}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="룸 선택"
                    options={roomOptions.map((room) => ({
                      label: room.roomName,
                      value: room.id,
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  label="시간 범위"
                  name="timeRange"
                  rules={[{ required: true, message: '시간 범위를 선택해주세요.' }]}
                >
                  <TimePicker.RangePicker
                    format="HH:mm"
                    minuteStep={30}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Card>

              <Card
                size="small"
                title="시간 슬롯 선택"
                style={{ marginTop: 16 }}
                extra={
                  <Space>
                    <Button size="small" onClick={handleSelectAllSlots}>
                      전체 선택
                    </Button>
                    <Button size="small" onClick={handleClearAllSlots}>
                      전체 해제
                    </Button>
                  </Space>
                }
              >
                <Space size={[8, 8]} wrap>
                  {slotOptions.map((minute) => {
                    const checked = selectedStartMinutes.includes(minute);
                    return (
                      <Tag
                        key={minute}
                        color={checked ? 'blue' : 'default'}
                        style={{
                          cursor: 'pointer',
                          userSelect: 'none',
                          padding: '6px 10px',
                          marginInlineEnd: 0,
                        }}
                        onClick={() => toggleMinute(minute)}
                      >
                        {minuteToLabel(minute)}
                      </Tag>
                    );
                  })}

                  {slotOptions.length === 0 && (
                    <Text type="secondary">선택 가능한 시간 슬롯이 없습니다.</Text>
                  )}
                </Space>
              </Card>
            </Col>

            <Col span={10}>
              <Card size="small" title="선택 결과 미리보기">
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  <div>
                    <Text strong>적용 기간</Text>
                    <div style={{ marginTop: 8 }}>
                      {watchedDateRange?.[0] && watchedDateRange?.[1] ? (
                        <Tag color="orange">
                          {watchedDateRange[0].format('YYYY-MM-DD')} ~ {watchedDateRange[1].format('YYYY-MM-DD')}
                        </Tag>
                      ) : (
                        <Text type="secondary">선택 없음</Text>
                      )}
                    </div>
                  </div>

                  <Divider style={{ margin: '4px 0' }} />
                  <div>
                    <Text strong>요일</Text>
                    <div style={{ marginTop: 8 }}>
                      <Space wrap>
                        {selectedWeekdayLabels.length > 0 ? (
                          selectedWeekdayLabels.map((label) => (
                            <Tag key={label} color="purple">
                              {label}
                            </Tag>
                          ))
                        ) : (
                          <Text type="secondary">선택 없음</Text>
                        )}
                      </Space>
                    </div>
                  </div>

                  <Divider style={{ margin: '4px 0' }} />

                  <div>
                    <Text strong>룸</Text>
                    <div style={{ marginTop: 8 }}>
                      <Space wrap>
                        {selectedRoomLabels.length > 0 ? (
                          selectedRoomLabels.map((label) => (
                            <Tag key={label} color="green">
                              {label}
                            </Tag>
                          ))
                        ) : (
                          <Text type="secondary">선택 없음</Text>
                        )}
                      </Space>
                    </div>
                  </div>

                  <Divider style={{ margin: '4px 0' }} />
                  
                  <div>
                    <Text strong>선택 슬롯</Text>
                    <div style={{ marginTop: 8 }}>
                      <Space wrap>
                        {selectedStartMinutes.length > 0 ? (
                          selectedStartMinutes.map((minute) => (
                            <Tag key={minute} color="blue">
                              {minuteToLabel(minute)}
                            </Tag>
                          ))
                        ) : (
                          <Text type="secondary">선택 없음</Text>
                        )}
                      </Space>
                    </div>
                  </div>
                        
                  <Divider style={{ margin: '4px 0' }} />
                  
                  <div>
                    <Text strong>예상 생성 단위</Text>
                    <div style={{ marginTop: 8 }}>
                      <Text>
                        요일 {watchedWeekdays?.length || 0}개 × 룸 {watchedRoomIds?.length || 0}개 ×
                        시간 {selectedStartMinutes.length}개
                      </Text>
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <Tag color="gold">총 {estimatedCount}개 기준</Tag>
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}